import { createContext, ReactNode, useContext, useEffect, useState } from "react"
import { API_BASE_URL } from "../config/apiBaseUrl"

export const DISPLAY_CURRENCIES = [
  ["USD", "US dollar"], ["GBP", "British pound"], ["CHF", "Swiss franc"],
  ["CNY", "Chinese yuan"], ["JPY", "Japanese yen"], ["KRW", "South Korean won"],
  ["INR", "Indian rupee"], ["SGD", "Singapore dollar"], ["HKD", "Hong Kong dollar"],
  ["TWD", "New Taiwan dollar"], ["THB", "Thai baht"], ["VND", "Vietnamese dong"],
  ["IDR", "Indonesian rupiah"], ["MYR", "Malaysian ringgit"], ["PHP", "Philippine peso"],
  ["BDT", "Bangladeshi taka"], ["PKR", "Pakistani rupee"], ["AED", "UAE dirham"],
  ["AUD", "Australian dollar"], ["CAD", "Canadian dollar"]
] as const
const STORAGE_KEY = "displayCurrency"
type Rates = Record<string, { rate: number; date: string }>
type CurrencyContext = {
  currency: string; selectCurrency: (value: string) => void
  rates: Rates; loading: boolean; error: boolean; retry: () => void
}
const Context = createContext<CurrencyContext>({ currency: "", selectCurrency: () => {}, rates: {}, loading: false, error: false, retry: () => {} })

export function DisplayCurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrency] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      return DISPLAY_CURRENCIES.some(([code]) => code === saved) ? saved! : ""
    } catch { return "" }
  })
  const [rates, setRates] = useState<Rates>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [revision, setRevision] = useState(0)
  const enabled = Boolean(currency)
  useEffect(() => {
    if (!enabled) return
    const controller = new AbortController()
    const load = async () => {
      setLoading(true)
      setError(false)
      try {
        const response = await fetch(`${API_BASE_URL}/api/exchange-rates`, { signal: controller.signal })
        if (!response.ok) throw new Error("Rates unavailable")
        const data = await response.json()
        if (data.base !== "EUR" || !data.rates || typeof data.rates !== "object") throw new Error("Invalid rates")
        if (!controller.signal.aborted) setRates(data.rates)
      } catch {
        if (!controller.signal.aborted) { setRates({}); setError(true) }
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }
    void load()
    const interval = window.setInterval(load, 6 * 60 * 60 * 1000)
    return () => { controller.abort(); window.clearInterval(interval) }
  }, [enabled, revision])
  const selectCurrency = (value: string) => {
    if (value && !DISPLAY_CURRENCIES.some(([code]) => code === value)) return
    setCurrency(value)
    try { localStorage.setItem(STORAGE_KEY, value) } catch { /* Selection still works without storage. */ }
  }
  return <Context.Provider value={{ currency, selectCurrency, rates, loading, error, retry: () => setRevision(value => value + 1) }}>{children}</Context.Provider>
}
export const useDisplayCurrency = () => useContext(Context)
